import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { ButtonProps } from '../jdsbutton/logic';
import { CTA } from '../jdscontentblock/interface';
import { Devices } from '../../service/Devices';
import { JDSDesktopBottomSheetInternalProps } from './generated';
import { BottomSheet as MobileBottomSheet } from '../jdsmobilebottomsheet/generated';
import { resolvePropObjectKeys } from '../../utils/componenthelpers';
import { useEscapeClose } from '../../hooks/useEscapeClose';

export interface BottomSheetProps
  extends Omit<
    JDSDesktopBottomSheetInternalProps,
    'height' | 'primaryCTA' | 'secondaryCTA' | 'onBack' | 'label'
  > {
  primaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  secondaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  onClose?: () => void;
  onRequestClose?: () => boolean;
  children?: React.ReactNode;

  /**
   * This is used to control header in case of stepper control type
   */
  stepperConfig?: {
    /**
     * Can be set to go to a specific step. default is 0
     */
    activeStep?: number;
    /**
     * default is 2
     */
    totalStep?: number;
    /**
     * Can be used to show the current progress. default is "($activeStep+1)/$totalStep"
     */
    indicatorText?: string;
    /**
     * This is called when back button is clicked.
     */
    onBack?: (activeStep?: number) => boolean;
  };
  height?: string | number;
}

export const PROP_MAP: IPROP_MAP<BottomSheetProps> = (props) => {
  return {
    primaryCTA: {
      properties: {
        primaryCTA: resolvePropObjectKeys(props.primaryCTA, 'title', ''),
        onPrimaryClick:
          (props.primaryCTA as CTA)?.onClick || props.onPrimaryClick
      }
    },
    secondaryCTA: {
      properties: {
        secondaryCTA: resolvePropObjectKeys(props.secondaryCTA, 'title', ''),
        onSecondaryClick:
          (props.secondaryCTA as CTA)?.onClick || props.onSecondaryClick
      }
    }
  };
};

export const withLogic =
  (
    Component: React.ComponentType<BottomSheetProps>
  ): React.FC<BottomSheetProps> =>
  (userProps: BottomSheetProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const { close, onRequestClose, onClose, ...rest } = props;
    const allowClose = !onRequestClose || onRequestClose();
    const closeFn = () => {
      if (allowClose) {
        onClose?.();
      }
    };
    const { mobile } = Devices.useMedia();

    useEscapeClose(closeFn, allowClose);
    if (mobile) {
      return <MobileBottomSheet {...userProps} />;
    }
    return (
      <Component
        {...{
          ...rest,
          onBack: () => {
            props.stepperConfig?.onBack?.(props.stepperConfig.activeStep);
          },
          label: props.stepperConfig?.indicatorText,
          _ariaModal:
            props.kind === 'overlay' || !props.close ? 'true' : 'false',
          _onBottomSheetClick: (ev: React.MouseEvent<HTMLDivElement>) =>
            ev.stopPropagation(),
          close,
          onCancel: () => {
            if (allowClose) {
              closeFn();
            }
          },
          onClose: () => {
            if (allowClose) {
              closeFn();
            }
          }
        }}
      />
    );
  };
